import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';
import { useAdmin } from '../../hooks/useAdmin';
import AdminListItem from './AdminListItem';
import AdminEmptyState from './AdminEmptyState';
import AdminPageLayout from './AdminPageLayout';
import AdminSearchBar from './AdminSearchBar';
import StatCard from '../shared/StatCard';
import YesNoPopup from '../shared/YesNoPopup';
import { toast } from 'sonner';
import '../../admin.css';

const PAGE_SIZE = 20;

export default function AdminUserManager() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('email');
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchResults, setSearchResults] = useState(null); // null = not searching, [] = no results
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showAddXPForm, setShowAddXPForm] = useState(false);
  const [newXPEntry, setNewXPEntry] = useState({
    title: '',
    value: 0,
    type: 'quest',
    status: 'completed'
  });
  const [xpActionLoading, setXpActionLoading] = useState(false);
  const [showDeleteQuestPopup, setShowDeleteQuestPopup] = useState(false);
  const [deleteQuestLoading, setDeleteQuestLoading] = useState(false);

  // Pagination state
  const [allUsers, setAllUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const searchInputRef = useRef(null);
  const listRef = useRef(null);
  const {
    getUserStats, searchUserByEmail, searchUserByUsername, searchUserByUID,
    addXPEntry, deleteXPEntry, deleteActiveQuest, fetchUsersPaginated, getUserCount
  } = useAdmin();

  // Load initial users and count on mount
  const loadInitialUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const [result, count] = await Promise.all([
        fetchUsersPaginated(PAGE_SIZE, null),
        getUserCount()
      ]);
      setAllUsers(result.users);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
      setTotalCount(count);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  }, [fetchUsersPaginated, getUserCount]);

  useEffect(() => {
    loadInitialUsers();
  }, [loadInitialUsers]);

  // Load more users (next page)
  const loadMoreUsers = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const result = await fetchUsersPaginated(PAGE_SIZE, lastDoc);
      setAllUsers(prev => [...prev, ...result.users]);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Error loading more users:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Infinite scroll — attach to the .admin-page-list parent
  useEffect(() => {
    const listEl = listRef.current?.closest('.admin-page-list');
    if (!listEl) return;

    const handleScroll = () => {
      if (searchResults !== null) return;
      const { scrollTop, scrollHeight, clientHeight } = listEl;
      if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loadingMore) {
        loadMoreUsers();
      }
    };

    listEl.addEventListener('scroll', handleScroll);
    return () => listEl.removeEventListener('scroll', handleScroll);
  }, [searchResults, hasMore, loadingMore, lastDoc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle user search based on search type
  const handleSearch = async (query) => {
    if (!query || !query.trim()) {
      setSearchResults(null);
      setSearchError('');
      return;
    }

    setSearching(true);
    setSearchError('');

    try {
      let user = null;

      switch (searchType) {
        case 'email':
          user = await searchUserByEmail(query);
          break;
        case 'username':
          user = await searchUserByUsername(query);
          break;
        case 'uid':
          user = await searchUserByUID(query);
          break;
        default:
          user = await searchUserByEmail(query);
      }

      if (user) {
        setSearchResults([user]);
        setSelectedUser(user);
      } else {
        setSearchResults([]);
        setSelectedUser(null);
        setSearchError(t('admin.users.userNotFound'));
      }
    } catch (error) {
      console.error('Error searching user:', error);
      setSearchError(t('admin.users.searchError'));
      setSearchResults([]);
      setSelectedUser(null);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults(null);
      setSearchError('');
    }
  };

  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value);
    setSearchResults(null);
    setSelectedUser(null);
    setSearchError('');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    handleSearch(searchQuery);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    setSearchError('');
    setSelectedUser(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return t('admin.users.never');
    return new Date(timestamp).toLocaleDateString();
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return t('admin.users.never');
    return new Date(timestamp).toLocaleString();
  };

  const handleAddXPEntry = async () => {
    if (!selectedUser || !newXPEntry.title.trim() || newXPEntry.value <= 0) {
      return;
    }

    setXpActionLoading(true);
    try {
      await addXPEntry(selectedUser.id, {
        ...newXPEntry,
        completedAt: Date.now(),
        createdAt: Date.now()
      });
      setNewXPEntry({ title: '', value: 0, type: 'quest', status: 'completed' });
      setShowAddXPForm(false);
    } catch (error) {
      console.error('Error adding XP entry:', error);
      setSearchError(t('admin.users.addXPError'));
    } finally {
      setXpActionLoading(false);
    }
  };

  const handleDeleteXPEntry = async (entryId) => {
    if (!selectedUser || !entryId) return;
    if (!window.confirm(t('admin.users.confirmDeleteXP'))) return;

    setXpActionLoading(true);
    try {
      await deleteXPEntry(selectedUser.id, entryId);
    } catch (error) {
      console.error('Error deleting XP entry:', error);
      setSearchError(t('admin.users.deleteXPError'));
    } finally {
      setXpActionLoading(false);
    }
  };

  // Determine which users to display
  const isSearchActive = searchResults !== null;
  const displayUsers = isSearchActive ? searchResults : allUsers;

  const renderUserList = () => {
    // Loading state
    if (loadingUsers && !isSearchActive) {
      return (
        <div className="admin-list-loading">
          <FontAwesomeIcon icon={ICONS.SPINNER} spin />
          <span>{t('admin.users.loadingUsers')}</span>
        </div>
      );
    }

    // Search error
    if (searchError && isSearchActive) {
      return (
        <AdminEmptyState
          icon={ICONS.EXCLAMATION_TRIANGLE}
          title={t('admin.users.searchError')}
          description={searchError}
        />
      );
    }

    // No results
    if (displayUsers.length === 0) {
      if (isSearchActive) {
        return (
          <AdminEmptyState
            icon={ICONS.USERS}
            title={t('admin.users.noUsersFound')}
            description={t('admin.users.tryDifferentEmail')}
          />
        );
      }
      return (
        <AdminEmptyState
          icon={ICONS.USERS}
          title={t('admin.users.noUsers')}
          description={t('admin.users.tryDifferentSearch')}
        />
      );
    }

    return (
      <>
        {displayUsers.map(user => {
          const stats = getUserStats(user);
          return (
            <AdminListItem
              key={user.id}
              title={user.username || t('admin.users.anonymousUser')}
              avatar={user.username?.charAt(0).toUpperCase() || 'U'}
              selected={selectedUser?.id === user.id}
              onClick={() => setSelectedUser(user)}
              meta={[
                { text: user.email },
                { icon: ICONS.CALENDAR, text: formatDate(user.createdAt) },
                { icon: ICONS.STAR, text: `${stats.totalXP} XP` },
              ]}
            />
          );
        })}
        {loadingMore && (
          <div className="admin-list-loading-more">
            <FontAwesomeIcon icon={ICONS.SPINNER} spin />
          </div>
        )}
      </>
    );
  };

  return (
    <AdminPageLayout
      title={t('admin.userManagement')}
      className="admin-user-manager"
      headerActions={
        <AdminSearchBar
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder={
            searchType === 'email' ? t('admin.users.searchByEmail') :
            searchType === 'username' ? t('admin.users.searchByUsername') :
            t('admin.users.searchByUID')
          }
          onSubmit={handleSearchSubmit}
          searchTypes={[
            { value: 'email', label: t('admin.users.searchTypeEmail') },
            { value: 'username', label: t('admin.users.searchTypeUsername') },
            { value: 'uid', label: t('admin.users.searchTypeUID') },
          ]}
          selectedType={searchType}
          onTypeChange={handleSearchTypeChange}
          submitLabel={t('admin.users.search')}
          submitting={searching}
          submitDisabled={!searchQuery.trim()}
          inputRef={searchInputRef}
          countBadge={
            isSearchActive && searchResults.length > 0 ? (
              <span className="admin-count-badge">
                {searchResults.length} {searchResults.length === 1 ? t('common.user') : t('common.users')} {t('admin.users.found')}
              </span>
            ) : !isSearchActive && totalCount > 0 ? (
              <span className="admin-count-badge">
                {totalCount} {totalCount === 1 ? t('common.user') : t('common.users')}
              </span>
            ) : null
          }
          actions={
            isSearchActive ? (
              <button
                type="button"
                className="admin-clear-search-btn"
                onClick={clearSearch}
                title="Clear search"
              >
                <FontAwesomeIcon icon={ICONS.TIMES} />
              </button>
            ) : null
          }
        />
      }
      listContent={
        <div ref={listRef}>
          {renderUserList()}
        </div>
      }
      detailContent={
        selectedUser ? (
          <div className="details-content">
            <div className="admin-detail-header">
                <div className="user-avatar large">
                  {selectedUser.username?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="user-title">
                  <h3>{selectedUser.username || t('admin.users.anonymousUser')}</h3>
                  <p>User ID: {selectedUser.id}</p>
                </div>
              </div>

              <div className="details-sections">
                {/* Basic Info */}
                <div className="admin-detail-section">
                  <h4>
                    <FontAwesomeIcon icon={ICONS.INFO_CIRCLE} />
                    {t('admin.users.basicInformation')}
                  </h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>{t('admin.users.username')}:</label>
                      <span>{selectedUser.username || t('admin.users.notSet')}</span>
                    </div>
                    <div className="info-item">
                      <label>{t('admin.users.created')}:</label>
                      <span>{formatDate(selectedUser.createdAt)}</span>
                    </div>
                    <div className="info-item">
                      <label>{t('admin.users.lastActive')}:</label>
                      <span>{formatDateTime(selectedUser.lastActive)}</span>
                    </div>
                    <div className="info-item">
                      <label>{t('admin.users.updated')}:</label>
                      <span>{formatDateTime(selectedUser.updatedAt)}</span>
                    </div>
                  </div>
                </div>

                {/* Quest Statistics */}
                <div className="admin-detail-section">
                  <h4>
                    <FontAwesomeIcon icon={ICONS.CHART_BAR} />
                    {t('admin.users.questStatistics')}
                  </h4>
                  <div className="stats-grid">
                    {(() => {
                      const stats = getUserStats(selectedUser);
                      return (
                        <>
                          <StatCard icon={ICONS.CHECK_CIRCLE} value={stats.completedCount} label={t('admin.users.completed')} />
                          <StatCard icon={ICONS.TIMES_CIRCLE} value={stats.failedCount} label={t('admin.users.failed')} />
                          <StatCard icon={ICONS.CLOCK} value={stats.activeCount} label={t('admin.users.active')} />
                          <StatCard icon={ICONS.STAR} value={stats.totalXP} label={t('admin.users.totalXP')} />
                          <StatCard icon={ICONS.PERCENT} value={`${stats.successRate}%`} label={t('admin.users.successRate')} />
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Active Quest */}
                {selectedUser.activeQuest && (
                  <div className="admin-detail-section">
                    <h4>
                      <FontAwesomeIcon icon={ICONS.CLOCK} />
                      {t('admin.users.activeQuest', 'Active Quest')}
                    </h4>
                    <div className="active-quest-card">
                      <div className="active-quest-info">
                        <div className="info-item">
                          <label>{t('admin.users.questId', 'Quest ID')}:</label>
                          <span>{selectedUser.activeQuest.id}</span>
                        </div>
                        {selectedUser.activeQuest.createdAt && (
                          <div className="info-item">
                            <label>{t('admin.users.startedAt', 'Started')}:</label>
                            <span>{formatDateTime(selectedUser.activeQuest.createdAt)}</span>
                          </div>
                        )}
                      </div>
                      <button
                        className="btn-danger"
                        onClick={() => setShowDeleteQuestPopup(true)}
                      >
                        <FontAwesomeIcon icon={ICONS.TRASH} />
                        {t('admin.users.removeActiveQuest', 'Remove Active Quest')}
                      </button>
                    </div>
                  </div>
                )}

                {/* XP History */}
                <div className="admin-detail-section">
                  <div className="section-header">
                    <h4>
                      <FontAwesomeIcon icon={ICONS.HISTORY} />
                      {t('admin.users.xpHistory')}
                    </h4>
                    <button
                      className="add-xp-btn"
                      onClick={() => setShowAddXPForm(true)}
                      title={t('admin.users.addXPEntry')}
                    >
                      <FontAwesomeIcon icon={ICONS.PLUS} />
                    </button>
                  </div>
                  <div className="xp-history">
                    {(() => {
                      const xpHistory = selectedUser.xpHistory || {};
                      const historyEntries = Object.entries(xpHistory);

                      if (historyEntries.length === 0) {
                        return (
                          <div className="no-xp-history">
                            <FontAwesomeIcon icon={ICONS.STAR} />
                            <span>{t('admin.users.noXPHistory')}</span>
                          </div>
                        );
                      }

                      return historyEntries
                        .sort(([,a], [,b]) => (b.updatedAt || 0) - (a.updatedAt || 0))
                        .map(([entryId, entryData]) => (
                          <div key={entryId} className={`xp-entry ${entryData.status || 'unknown'}`}>
                            <div className="xp-entry-info">
                              <div className="xp-entry-header">
                                <span className="xp-entry-id">{entryId.slice(-8)}</span>
                                <span className={`xp-entry-status status-${entryData.status || 'unknown'}`}>
                                  {entryData.status || 'unknown'}
                                </span>
                              </div>
                              <div className="xp-entry-details">
                                <span className="xp-entry-type">{entryData.type || 'quest'}</span>
                                {entryData.title && <span className="xp-entry-title">{entryData.title}</span>}
                                <span className="xp-entry-date">
                                  {entryData.updatedAt ? new Date(entryData.updatedAt).toLocaleString() : 'Unknown date'}
                                </span>
                              </div>
                            </div>
                            <div className="xp-entry-actions">
                              <span className="xp-entry-value">{entryData.value || 0} XP</span>
                              <button
                                className="delete-xp-btn"
                                onClick={() => handleDeleteXPEntry(entryId)}
                                title={t('admin.users.deleteXPEntry')}
                              >
                                <FontAwesomeIcon icon={ICONS.TRASH} />
                              </button>
                            </div>
                          </div>
                        ));
                    })()}
                  </div>
                </div>

                {/* Add XP Entry Form */}
                {showAddXPForm && (
                  <div className="admin-detail-section">
                    <h4>
                      <FontAwesomeIcon icon={ICONS.PLUS} />
                      {t('admin.users.addXPEntry')}
                    </h4>
                    <div className="add-xp-form">
                      <div className="form-row">
                        <div className="form-group">
                          <label>{t('admin.users.xpEntryTitle')}:</label>
                          <input
                            type="text"
                            value={newXPEntry.title}
                            onChange={(e) => setNewXPEntry({...newXPEntry, title: e.target.value})}
                            placeholder={t('admin.users.xpEntryTitlePlaceholder')}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('admin.users.xpValue')}:</label>
                          <input
                            type="number"
                            value={newXPEntry.value}
                            onChange={(e) => setNewXPEntry({...newXPEntry, value: parseInt(e.target.value) || 0})}
                            min="1"
                            placeholder="100"
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>{t('admin.users.xpEntryType')}:</label>
                          <select
                            value={newXPEntry.type}
                            onChange={(e) => setNewXPEntry({...newXPEntry, type: e.target.value})}
                          >
                            <option value="quest">{t('admin.users.typeQuest')}</option>
                            <option value="bonus">{t('admin.users.typeBonus')}</option>
                            <option value="achievement">{t('admin.users.typeAchievement')}</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>{t('admin.users.xpEntryStatus')}:</label>
                          <select
                            value={newXPEntry.status}
                            onChange={(e) => setNewXPEntry({...newXPEntry, status: e.target.value})}
                          >
                            <option value="completed">{t('admin.users.statusCompleted')}</option>
                            <option value="failed">{t('admin.users.statusFailed')}</option>
                            <option value="active">{t('admin.users.statusActive')}</option>
                          </select>
                        </div>
                      </div>
                      <div className="form-actions">
                        <button
                          className="btn-primary"
                          onClick={handleAddXPEntry}
                          disabled={xpActionLoading || !newXPEntry.title.trim() || newXPEntry.value <= 0}
                        >
                          {xpActionLoading ? (
                            <FontAwesomeIcon icon={ICONS.SPINNER} spin />
                          ) : (
                            t('admin.users.addXPEntry')
                          )}
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => setShowAddXPForm(false)}
                          disabled={xpActionLoading}
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

            <YesNoPopup
              isOpen={showDeleteQuestPopup}
              title={t('admin.users.removeActiveQuest', 'Remove Active Quest')}
              message={t('admin.users.confirmDeleteActiveQuest', 'Are you sure you want to remove this user\'s active quest? This action cannot be undone.')}
              confirmLabel={t('common.yes', 'Yes')}
              cancelLabel={t('common.no', 'No')}
              variant="danger"
              loading={deleteQuestLoading}
              onCancel={() => setShowDeleteQuestPopup(false)}
              onConfirm={async () => {
                setDeleteQuestLoading(true);
                try {
                  await deleteActiveQuest(selectedUser.id);
                  toast.success(t('admin.users.activeQuestDeleted', 'Active quest removed successfully'));
                  setShowDeleteQuestPopup(false);
                } catch (err) {
                  toast.error(t('admin.users.activeQuestDeleteError', 'Failed to remove active quest'));
                } finally {
                  setDeleteQuestLoading(false);
                }
              }}
            />
          </div>
        ) : (
          <AdminEmptyState
            icon={ICONS.USER}
            description={t('admin.users.noSelection')}
          />
        )
      }
    />
  );
}
